import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import GoalHeader from './GoalHeader';
import GoalStatusSnapshot from './GoalStatusSnapshot';
import GoalProgress from './GoalProgress';
import GoalHierarchy from './GoalHierarchy';
import GoalNotes from './GoalNotes';

export default function GoalDetailPanel({
  goal,
  onClose,
  projects,
  tasks,
  onUpdateGoal,
  onAddProject,
  onAddTask,
  onCompleteTask,
  onUncompleteTask,
  onEditGoal,
  onDeleteGoal,
  isAdmin
}) {
  const panelRef = useRef(null);

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Handle click outside to close (on backdrop)
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Get projects for this goal
  const goalProjects = projects.filter(p => p.goalId === goal.id && p.status === 'active');

  // Get all tasks for this goal (direct + via projects)
  const directTasks = tasks.filter(t => t.goalId === goal.id && !t.projectId);
  const projectTasksMap = {};
  goalProjects.forEach(p => {
    projectTasksMap[p.id] = tasks.filter(t => t.projectId === p.id);
  });

  // Calculate overall stats
  const allGoalTasks = [
    ...directTasks,
    ...goalProjects.flatMap(p => projectTasksMap[p.id] || [])
  ];

  const totalTasks = allGoalTasks.length;
  const completedTasks = allGoalTasks.filter(t => t.completedAt).length;
  const activeTasks = allGoalTasks.filter(t => !t.completedAt && !t.isSomeday && !t.waitingFor && !t.delegatedTo).length;
  const blockedTasks = allGoalTasks.filter(t => !t.completedAt && (t.isSomeday || t.waitingFor || t.delegatedTo)).length;

  const progressPercent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <>
      {/* Backdrop for mobile */}
      <div
        className="fixed inset-0 bg-black/20 z-40 md:hidden"
        onClick={handleBackdropClick}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed top-0 right-0 h-full bg-surface-base border-l border-border shadow-xl z-50
                   w-full md:w-[70%] lg:w-[65%] xl:w-[60%]
                   transform transition-transform duration-300 ease-out
                   overflow-y-auto"
        style={{ animation: 'slideIn 0.3s ease-out' }}
      >
        {/* Close button - mobile */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg bg-surface-hover text-text-secondary
                     hover:bg-surface-active hover:text-text-primary transition-all md:hidden z-10"
        >
          <X size={20} />
        </button>

        {/* Panel content */}
        <div className="p-6 pb-24">
          {/* Header */}
          <GoalHeader
            goal={goal}
            progressPercent={progressPercent}
            onClose={onClose}
            onEdit={() => onEditGoal(goal)}
            onDelete={() => onDeleteGoal(goal)}
            onToggleComplete={() => onUpdateGoal(goal.id, { completed: !goal.completed })}
            isAdmin={isAdmin}
          />

          {/* Status Snapshot */}
          <GoalStatusSnapshot
            total={totalTasks}
            completed={completedTasks}
            active={activeTasks}
            blocked={blockedTasks}
          />

          {/* Progress Bar */}
          <GoalProgress
            progressPercent={progressPercent}
            projects={goalProjects}
            projectTasksMap={projectTasksMap}
          />

          {/* Hierarchy */}
          <GoalHierarchy
            goal={goal}
            projects={goalProjects}
            directTasks={directTasks}
            projectTasksMap={projectTasksMap}
            onAddProject={() => onAddProject(goal.id)}
            onAddTaskToProject={onAddTask}
            onAddTaskToGoal={() => onAddTask(null, goal.id)}
            onCompleteTask={onCompleteTask}
            onUncompleteTask={onUncompleteTask}
            isAdmin={isAdmin}
          />

          {/* Notes */}
          <GoalNotes
            goal={goal}
            onUpdateNotes={(notes) => onUpdateGoal(goal.id, { notes })}
            isAdmin={isAdmin}
          />
        </div>
      </div>

      {/* CSS for slide animation */}
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
}
